import React, { useState, useEffect } from 'react';
import { Button, Space, Tag, Tooltip, Popover, Alert, Spin } from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { StateMachineService } from '../../services/api/stateMachineService';
import { AvailableTransition, TransitionValidationResult } from '../../types/stateMachine';
import { ArtifactItem } from '../../types/artifactItems';

interface AvailableTransitionsProps {
  artifactId: string;
  item: ArtifactItem;
  onTransitionSelect?: (transition: AvailableTransition) => void;
  onTransitionExecute?: (transition: AvailableTransition) => void;
  compact?: boolean;
}

export const AvailableTransitions: React.FC<AvailableTransitionsProps> = ({
  artifactId,
  item,
  onTransitionSelect,
  onTransitionExecute,
  compact = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [availableTransitions, setAvailableTransitions] = useState<AvailableTransition[]>([]);
  const [validations, setValidations] = useState<Map<string, TransitionValidationResult>>(
    new Map()
  );

  useEffect(() => {
    if (item.id) {
      void loadAvailableTransitions();
    }
  }, [item.id, artifactId]);

  const loadAvailableTransitions = async () => {
    try {
      setLoading(true);
      const transitions = await StateMachineService.getAvailableTransitions(artifactId, item.id);
      setAvailableTransitions(transitions);

      // Validate all transitions
      const validationPromises = transitions.map(async (transition) => {
        try {
          const validation = await StateMachineService.validateTransition(
            artifactId,
            item.id,
            transition.id
          );
          return { transitionId: transition.id, validation };
        } catch (error) {
          console.error(`Failed to validate transition ${transition.id}:`, error);
          return {
            transitionId: transition.id,
            validation: {
              can_execute: false,
              errors: ['Failed to validate transition'],
              warnings: [],
              conditions_met: false,
            } as TransitionValidationResult,
          };
        }
      });

      const validationResults = await Promise.all(validationPromises);
      const validationMap = new Map<string, TransitionValidationResult>();
      validationResults.forEach(({ transitionId, validation }) => {
        validationMap.set(transitionId, validation);
      });
      setValidations(validationMap);
    } catch (error: any) {
      console.error('Error loading available transitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionClick = (transition: AvailableTransition) => {
    if (onTransitionExecute) {
      onTransitionExecute(transition);
    } else if (onTransitionSelect) {
      onTransitionSelect(transition);
    }
  };

  const getValidationPopover = (transition: AvailableTransition) => {
    const validation = validations.get(transition.id);
    if (!validation) return null;

    return (
      <div style={{ maxWidth: 300 }}>
        {validation.errors.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <strong>Errors:</strong>
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              {validation.errors.map((error, idx) => (
                <li key={idx} style={{ fontSize: 12 }}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}
        {validation.warnings.length > 0 && (
          <div>
            <strong>Warnings:</strong>
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              {validation.warnings.map((warning, idx) => (
                <li key={idx} style={{ fontSize: 12 }}>
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}
        {validation.condition_results && validation.condition_results.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <strong>Conditions:</strong>
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              {validation.condition_results.map((result, idx) => (
                <li key={idx} style={{ fontSize: 12 }}>
                  {result.condition_name}: {result.met ? '✓' : '✗'}
                  {result.message && ` - ${result.message}`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <Spin size="small" />;
  }

  if (availableTransitions.length === 0) {
    return compact ? null : (
      <Alert message="No transitions available" type="info" showIcon style={{ marginTop: 8 }} />
    );
  }

  return (
    <Space wrap size="small">
      {availableTransitions.map((transition) => {
        const validation = validations.get(transition.id);
        const canExecute = validation?.can_execute ?? false;
        const hasIssues = validation && (!canExecute || validation.warnings.length > 0);

        const button = (
          <Button
            key={transition.id}
            type={canExecute ? 'primary' : 'default'}
            icon={<PlayCircleOutlined />}
            disabled={!canExecute}
            onClick={() => handleTransitionClick(transition)}
            size={compact ? 'small' : 'middle'}
          >
            {transition.transition_name ?? transition.name}
          </Button>
        );

        if (hasIssues) {
          return (
            <Popover
              key={transition.id}
              content={getValidationPopover(transition)}
              title="Transition Validation"
              trigger="hover"
            >
              <span>
                {button}
                <Tooltip title={canExecute ? 'Ready' : 'Cannot Execute'}>
                  {canExecute ? (
                    <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 4 }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />
                  )}
                </Tooltip>
              </span>
            </Popover>
          );
        }

        return (
          <Tooltip
            key={transition.id}
            title={
              transition.description ||
              `${(transition as any).from_state_name || transition.from_state_name || 'Unknown'} → ${(transition as any).to_state_name || transition.to_state_name || 'Unknown'}`
            }
          >
            {button}
          </Tooltip>
        );
      })}
    </Space>
  );
};

